import { useState, useMemo } from 'react'
import {
  Stack,
  TextField,
  MenuItem,
  Box,
  Select,
  InputLabel,
  FormControl,
  Button,
  InputAdornment,
  useTheme,
} from '@mui/material'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'

import { i18WithDefault as t } from '@shared/locales'
import { Types } from 'service-api'
import { getUniqueBenefitOffers } from './helper'
import { format } from 'date-fns'
import { CalendarIconAvatar } from 'icon_avatar/icon_avatar'

const BenefitFilter = ({
  selectedBenefitOffer,
  setSelectedBenefitOffer,
  benefitOffers,
  hideBenefitFilter,
  inline = false,
}: {
  selectedBenefitOffer: string
  setSelectedBenefitOffer: (value: string) => void
  benefitOffers: Array<{ value: string; label: string }>
  hideBenefitFilter: boolean
  inline?: boolean
}) => {
  if (hideBenefitFilter) return null

  return (
    <FormControl sx={{ minWidth: inline ? 200 : 250, ...(inline ? { width: { xs: '100%', sm: 'auto' } } : {}) }}>
      <InputLabel id="benefitOffer-label">{t('global.label.benefit')}</InputLabel>
      <Select
        name="benefitOffer"
        labelId="benefitOffer-label"
        label={t('global.label.benefit')}
        value={selectedBenefitOffer}
        onChange={(e) => setSelectedBenefitOffer(e.target.value)}
      >
        <MenuItem value={t('global.label.all')}>{t('global.label.all')}</MenuItem>
        {benefitOffers.map((offer, idx) => (
          <MenuItem key={`${offer.value}_${idx}`} value={offer.value}>
            {offer.label} {t('membership.benefit.discount.small')}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const DateRangeFilter = ({
  selectedStartDate,
  selectedEndDate,
  setSelectedStartDate,
  setSelectedEndDate,
  inline = false,
}: {
  selectedStartDate?: Date
  selectedEndDate?: Date
  setSelectedStartDate: (date?: Date) => void
  setSelectedEndDate: (date?: Date) => void
  inline?: boolean
}) => {
  const theme = useTheme()
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangePicker
        inputFormat="MM/dd/yyyy"
        desktopModeMediaQuery={theme.breakpoints.up('md')}
        value={[selectedStartDate ? selectedStartDate : null, selectedEndDate ? selectedEndDate : null]}
        onChange={(newValue) => {
          setSelectedStartDate(newValue[0] || undefined)
          setSelectedEndDate(newValue[1] || undefined)
        }}
        renderInput={(startProps, _endProps) => {
          let displayValue = ''
          if (selectedStartDate && selectedEndDate) {
            displayValue = `${format(selectedStartDate, 'MM/dd/yyyy')} - ${format(selectedEndDate, 'MM/dd/yyyy')}`
          } else if (selectedStartDate) {
            displayValue = format(selectedStartDate, 'MM/dd/yyyy')
          }
          return (
            <TextField
              {...startProps}
              label={t('membership.benefit.visitDateRange')}
              sx={{ ...(inline ? { mb: 0, width: { xs: '100%', sm: 'auto' } } : { minWidth: 260 }) }}
              InputProps={{
                ...startProps.InputProps,
                endAdornment: (
                  <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                    {inline ? <CalendarTodayIcon /> : <CalendarIconAvatar />}
                  </InputAdornment>
                ),
              }}
              inputProps={{
                ...startProps.inputProps,
                placeholder: 'MM/DD/YYYY',
                value: displayValue,
              }}
            />
          )
        }}
      />
    </LocalizationProvider>
  )
}

export const BenefitConsumptionTableFilters = ({
  benefitConsumptionRecords,
  onApplyFilters,
  onClearFilters,
  hideBenefitFilter,
  inline = false,
}: {
  benefitConsumptionRecords?: Types.APIBenefitConsumptionRecordAdminDtoResponse
  onApplyFilters?: (filters: { benefitOffer: string; startDate?: Date; endDate?: Date }) => void
  onClearFilters?: () => void
  hideBenefitFilter?: boolean
  inline?: boolean
}) => {
  const [selectedBenefitOffer, setSelectedBenefitOffer] = useState(t('global.label.all'))
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(undefined)
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(undefined)
  const hasChanges = Boolean(selectedBenefitOffer !== t('global.label.all') || selectedStartDate || selectedEndDate)

  const handleApplyFilters = () => {
    onApplyFilters?.({
      benefitOffer: selectedBenefitOffer,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
    })
  }

  const resetFilters = () => {
    setSelectedStartDate(undefined)
    setSelectedEndDate(undefined)
    setSelectedBenefitOffer(t('global.label.all'))
    onClearFilters?.()
  }

  const benefitOffers = useMemo(() => getUniqueBenefitOffers(benefitConsumptionRecords), [benefitConsumptionRecords])

  const benefitFilter = (
    <BenefitFilter
      selectedBenefitOffer={selectedBenefitOffer}
      setSelectedBenefitOffer={setSelectedBenefitOffer}
      benefitOffers={benefitOffers}
      hideBenefitFilter={hideBenefitFilter || false}
      inline={inline}
    />
  )

  const dateRangeFilter = (
    <DateRangeFilter
      selectedStartDate={selectedStartDate}
      selectedEndDate={selectedEndDate}
      setSelectedStartDate={setSelectedStartDate}
      setSelectedEndDate={setSelectedEndDate}
      inline={inline}
    />
  )

  if (inline) {
    // Inline layout for use in table dropdownFilters
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          {benefitFilter}
          {dateRangeFilter}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: { xs: 1, sm: 0 } }}>
          <Button disabled={!hasChanges} onClick={resetFilters} color="primary" sx={{ px: 3 }}>
            {t('global.label.clear')}
          </Button>
          <Button disabled={!hasChanges} variant="contained" onClick={handleApplyFilters} sx={{ px: 3 }}>
            {t('global.label.search')}
          </Button>
        </Stack>
      </Stack>
    )
  }

  // Default layout for standalone use
  return (
    <Box sx={{ mb: 4 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          {benefitFilter}
          {dateRangeFilter}
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            disabled={!hasChanges}
            variant="contained"
            onClick={handleApplyFilters}
            color="secondary"
            sx={{ py: 1, px: 2 }}
          >
            {t('global.label.applyFilters')}
          </Button>
          <Button
            disabled={!hasChanges}
            variant="outlined"
            onClick={resetFilters}
            color="primary"
            sx={{ py: 1, px: 2 }}
          >
            {t('global.label.clearFilters')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
